/**
 * Composant d'indicateur de statut hors ligne
 * Affiche le statut de connexion et les données en attente de synchronisation
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff, Cloud, CloudOff, RefreshCw, AlertCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { initOfflineDB, getUnsyncedCount, syncAllPendingData } from '../services/offlineService';
import { getToken } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function OfflineStatusIndicator({ 
  showDetails = false, 
  position = 'bottom-right',
  autoSync = true 
}) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);

  // Mettre à jour le compteur de données non synchronisées
  const updateUnsyncedCount = useCallback(async () => {
    try {
      const count = await getUnsyncedCount();
      setUnsyncedCount(count);
    } catch {
    }
  }, []);

  // Synchroniser les données
  const handleSync = useCallback(async () => {
    if (!isOnline) {
      toast.error('Impossible de synchroniser hors ligne');
      return;
    }

    const token = getToken();
    if (!token) {
      toast.warning('Connexion requise pour synchroniser');
      return;
    }

    setIsSyncing(true);
    setSyncError(null);

    try {
      const result = await syncAllPendingData(API, token);
      
      if (result.success) {
        setLastSyncTime(new Date());
        await updateUnsyncedCount();
        
        const { results } = result;
        const totalSynced = (results?.simulations?.synced || 0) + (results?.ftpe_drafts?.synced || 0);
        
        if (totalSynced > 0) {
          toast.success(`${totalSynced} élément(s) synchronisé(s) avec succès`);
        } else if (results?.total_pending === 0) {
          toast.info('Toutes les données sont déjà synchronisées');
        }
      } else {
        setSyncError(result.error || 'Erreur de synchronisation');
        toast.error('Erreur lors de la synchronisation');
      }
    } catch (error) {
      setSyncError(error.message);
      toast.error('Erreur lors de la synchronisation');
    } finally {
      setIsSyncing(false);
    }
  }, [isOnline, updateUnsyncedCount]);

  // Écouter les changements de connexion
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Connexion rétablie !', {
        description: 'Synchronisation automatique en cours...',
        duration: 3000
      });
      
      // Synchroniser automatiquement après reconnexion
      if (autoSync) {
        setTimeout(() => handleSync(), 1500);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Mode hors ligne activé', {
        description: 'Vos données seront sauvegardées localement',
        duration: 5000
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [autoSync, handleSync]);

  // Initialiser IndexedDB et mettre à jour le compteur
  useEffect(() => {
    const init = async () => {
      try {
        await initOfflineDB();
        await updateUnsyncedCount();
      } catch {
      }
    };
    init();
  }, [updateUnsyncedCount]);

  // Écouter les messages du Service Worker pour la sync
  useEffect(() => {
    const handleSWMessage = (event) => {
      if (event.data?.type === 'TRIGGER_SYNC' || event.data?.type === 'BACKGROUND_SYNC') {
        if (isOnline && autoSync) {
          handleSync();
        }
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, [isOnline, autoSync, handleSync]);

  // Mettre à jour périodiquement le compteur
  useEffect(() => {
    const interval = setInterval(updateUnsyncedCount, 30000); // Toutes les 30 secondes
    return () => clearInterval(interval);
  }, [updateUnsyncedCount]);

  // Position du composant
  const positionClasses = {
    'bottom-right': 'fixed bottom-4 right-4',
    'bottom-left': 'fixed bottom-4 left-4',
    'top-right': 'fixed top-4 right-4',
    'top-left': 'fixed top-4 left-4',
    'inline': 'relative'
  };

  // Mode compact (inline dans le header)
  if (!showDetails) {
    return (
      <div className="flex items-center gap-2">
        {/* Indicateur de connexion */}
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all ${
          isOnline 
            ? 'bg-green-100 text-green-700' 
            : 'bg-amber-100 text-amber-700 animate-pulse'
        }`}>
          {isOnline ? (
            <>
              <Wifi className="h-3 w-3" />
              <span className="hidden sm:inline">En ligne</span>
            </>
          ) : (
            <>
              <WifiOff className="h-3 w-3" />
              <span className="hidden sm:inline">Hors ligne</span>
            </>
          )}
        </div>

        {/* Badge données non synchronisées */}
        {unsyncedCount > 0 && (
          <Badge 
            variant="outline" 
            className={`text-xs cursor-pointer transition-all ${
              isOnline 
                ? 'border-blue-300 text-blue-600 hover:bg-blue-50' 
                : 'border-amber-300 text-amber-600'
            }`}
            onClick={isOnline ? handleSync : undefined}
          >
            {isSyncing ? (
              <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <CloudOff className="h-3 w-3 mr-1" />
            )}
            {unsyncedCount} en attente
          </Badge>
        )}
      </div>
    );
  }

  // Mode détaillé (widget flottant)
  return (
    <div className={`${positionClasses[position]} z-50`}>
      <div className={`bg-white rounded-xl shadow-lg border p-4 min-w-[280px] transition-all ${
        isOnline ? 'border-gray-200' : 'border-amber-300 ring-2 ring-amber-100'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {isOnline ? (
              <div className="p-1.5 bg-green-100 rounded-lg">
                <Wifi className="h-4 w-4 text-green-600" />
              </div>
            ) : (
              <div className="p-1.5 bg-amber-100 rounded-lg animate-pulse">
                <WifiOff className="h-4 w-4 text-amber-600" />
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-gray-800">
                {isOnline ? 'Connecté' : 'Mode hors ligne'}
              </h4>
              <p className="text-xs text-gray-500">
                {isOnline 
                  ? 'Vos données sont synchronisées' 
                  : 'Les données seront sauvegardées localement'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Statistiques */}
        <div className="bg-gray-50 rounded-lg p-3 mb-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Données en attente</span>
            <Badge variant={unsyncedCount > 0 ? 'default' : 'secondary'}>
              {unsyncedCount}
            </Badge>
          </div>
          
          {lastSyncTime && (
            <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
              <span>Dernière sync</span>
              <span>{lastSyncTime.toLocaleTimeString('fr-FR')}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={isOnline ? 'default' : 'secondary'}
            className="flex-1"
            onClick={handleSync}
            disabled={!isOnline || isSyncing || unsyncedCount === 0}
          >
            {isSyncing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                Sync...
              </>
            ) : (
              <>
                <Cloud className="h-4 w-4 mr-1.5" />
                Synchroniser
              </>
            )}
          </Button>
        </div>

        {/* Erreur */}
        {syncError && (
          <div className="mt-2 p-2 bg-red-50 rounded-lg flex items-center gap-2 text-xs text-red-600">
            <AlertCircle className="h-3.5 w-3.5" />
            <span>{syncError}</span>
          </div>
        )}

        {/* Info hors ligne */}
        {!isOnline && (
          <div className="mt-2 p-2 bg-amber-50 rounded-lg text-xs text-amber-700">
            💡 Continuez à travailler ! Vos simulations seront automatiquement synchronisées à la reconnexion.
          </div>
        )}
      </div>
    </div>
  );
}

// Hook personnalisé pour utiliser le statut offline
export function useOfflineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [unsyncedCount, setUnsyncedCount] = useState(0);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Charger le compteur initial
    getUnsyncedCount().then(setUnsyncedCount).catch(() => {});

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { isOnline, unsyncedCount };
}
